import { type HighlightedCell, useActiveGridHighlight } from "@/grid/GridCellHighlightProvider";
import { useAppDispatch } from "@/reducer";
import { discardCountryPlot, updateCountryPlotState, useCountryPlot } from "@/reducer/CountryPlotState";
import type { WeatherPlotSeries } from "@/reducer/client-models";
import type { CountryPlotType } from "@/reducer/client-models/CountryPlot";
import type { CountryPlotCustomOptions, ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import type { SubmitHandler } from "react-hook-form";
import { useFormWithUtils } from "../../../SmartFormUtils/FormHooks/useFormUtils";
import type { BaseFormProps } from "../../../SmartFormUtils/commonTypeUtils";

type CountryPlotPatch = Partial</*CountryPlotCreate | */ CountryPlotType>;
/**
 * Form data passed from the child components.
 */
export type CountryPlotFormData = {
  // Snake case as they get passed as layer props directly. TODO not nice
  plotData: {
    site_id: string;
    model: string;
    title?: string | null;
    titleStyle?: ElementStyle | null;
    plotSeries: WeatherPlotSeries[];
    datetime_from: string | null;
    datetime_to: string | null;
    resolution: string | null;
    custom_options: CountryPlotCustomOptions | null;
  };
  // Form internal data to construct the above data.
  internal?: {
    defaultParameter: string; //TODO: used for the parameter switch (assume plotSeries has only one in the list)
  };
};

type CountryPlotFormProps = {
  defaultFormData: CountryPlotPatch;
} & BaseFormProps<CountryPlotFormData> & {
    onRemove: () => void;

    setHighlighted: (cell: HighlightedCell | undefined) => void;
  } & ReturnType<typeof useFormWithUtils<CountryPlotFormData>>;

/**
 * Handles all the logic for map background implementation update.
 * Note:
 * This is a controller, meaning that we should generally NOT delegate layer creation logic
 * to the parent component by passing callback from parent etc.
 */
export function useCountryPlotForm(toolId: number): CountryPlotFormProps {
  const plot = useCountryPlot(toolId);
  const { setHighlighted } = useActiveGridHighlight();
  const formUtils = useFormWithUtils<CountryPlotFormData>({
    mode: "onChange",
    values: {
      plotData: plot,
    },
  });

  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<CountryPlotFormData> = (formData) => {
    dispatch(
      updateCountryPlotState({
        id: plot.id,
        countryPlot: {
          ...formData.plotData,
          id: plot.id,
        },
      }),
    );
  };

  const onRemove = () => {
    dispatch(discardCountryPlot({ id: toolId }));
  };

  return {
    defaultFormData: plot,
    submitMode: "onChange",
    onSubmit,
    onRemove,
    setHighlighted,
    ...formUtils,
  };
}
